import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  LinearProgress,
  SelectChangeEvent,
  Alert,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { VehicleDocument } from '../../types/vehicle';
import vehicleService from '../../services/vehicleService';

interface DocumentUploadProps {
  vehicleId: string;
  organizationId: string;
  open: boolean;
  onClose: () => void;
  onUploadComplete: (document: VehicleDocument) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  vehicleId,
  organizationId,
  open,
  onClose,
  onUploadComplete,
}) => {
  const [docType, setDocType] = useState<VehicleDocument['type']>('insurance');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = (event: SelectChangeEvent) => {
    setDocType(event.target.value as VehicleDocument['type']);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a PDF, image, or Word document.');
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const document = await vehicleService.uploadDocument(
        vehicleId,
        organizationId,
        selectedFile,
        docType
      );
      onUploadComplete(document);
      onClose();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to upload document. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setDocType('insurance');
    onClose();
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Document Type</InputLabel>
            <Select
              value={docType}
              onChange={handleTypeChange}
              label="Document Type"
            >
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="registration">Registration</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />

          <Button
            variant="outlined"
            onClick={triggerFileInput}
            startIcon={<UploadIcon />}
            fullWidth
          >
            Select File
          </Button>

          {selectedFile && (
            <Box sx={{ typography: 'body2', mt: 1 }}>
              Selected file: {selectedFile.name}
            </Box>
          )}

          <Box sx={{ typography: 'caption', color: 'text.secondary', mt: 1 }}>
            Supported formats: PDF, Word documents, and images (JPG, PNG)
            <br />
            Maximum file size: 10MB
          </Box>
        </Box>

        {uploading && <LinearProgress sx={{ mb: 2 }} />}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!selectedFile || uploading}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUpload;
